import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { useLoaderData, useLocation, useNavigate, useParams } from 'react-router-dom';
import 'plyr/dist/plyr.css';
import { LessonHeader, ContentType, Text, useDeviceSize, Icon } from '@atlassian/learning-components';
import { css } from '@compiled/react';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { HelmetContext } from 'src/components/Helmet';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { getInspectModeData } from 'src/utils/contentful';
import { sendScreenEvent } from 'src/utils/analytics';
import badge from 'src/assets/images/badge.svg';
import { useSaveRecentlyViewed } from 'src/hooks/useSaveRecentlyViewed';
import { LearningProgressStatus, ProgressForCourses } from 'src/services/http/progress';
import { getNextCourse } from 'src/pages/LessonPage/children/utils/nextCourseUtils';
import { NextCourse } from 'src/pages/LessonPage/types/nextCourse';
import { isStandalone } from 'src/pages/LessonPage/utils/isStandalone';
import { ContentHubType } from 'src/constants';
import { BreadcrumbsContainer } from 'src/components/BreadcrumbsContainer';
import { useSidebarVisibility } from 'src/context/SidebarVisibilityContext';
import { MarkdownWrapper } from 'src/components/MarkdownWrapper';
import { getSlug } from 'src/utils/slug';
import { useProgressState } from 'src/store/progress/progressStore';
import { useRedirectObject } from 'src/hooks/useRedirectObject';
import { getHubUrl } from 'src/utils/getUrl';

import { useLessonInteractionTracking } from '../LessonPage/children/hooks/useLessonInteractionTracking';
import { Quiz } from './children/Quiz';
import { Sidebar } from './children/Sidebar';
import { LessonLoaderProps } from './loader';
import { NextLesson } from './children/NextLesson';
import { BadgeModal } from './children/BadgeModal';
import { LessonSection } from './children/LessonSection';
import { getBackgroundColors, navigateToSection } from './utils/utils';
import { Audio } from './children/Audio';
import { useIntersectionObserverForProgressTracking } from './children/hooks/useIntersectionObserverForProgressTracking';
import { scrollToSection } from './children/utils/sidebarUtils';

export const LessonPage: React.FC = () => {
  const { course, lesson, learningPath } = useLoaderData() as LessonLoaderProps;

  const deviceWidth = useDeviceSize();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const helmet = useContext(HelmetContext);

  const pathSlugFromUrl = params?.pathId;

  const [currentUrl, setCurrentUrl] = useState<string>(globalThis.location.href);
  const [nextCourse, setNextCourse] = useState<NextCourse | null>(null);
  const [isOpenedAudioPlayer, setIsOpenedAudioPlayer] = useState(false);

  const [isLessonRecentlyCompleted, setIsLessonRecentlyCompleted] = useState(false);

  const { user, isLoading, hasError } = useAtlassianUser();
  const { onLessonStarted, onLessonCompletion } = useLessonInteractionTracking();

  const isStandaloneLesson = isStandalone();

  const { isSidebarVisible, setIsSidebarVisible } = useSidebarVisibility();
  const hasNavigatedToSection = useRef(false);

  const isAllLearningPathsHubIncluded = location.pathname?.includes(getHubUrl(ContentHubType.LEARNING_PATHS));
  const isLearningPathIncluded = location.pathname?.includes('path');
  const isBreadcrumbVisible = isAllLearningPathsHubIncluded || (isLearningPathIncluded && !isStandaloneLesson);

  const courseSlug = getSlug(course);
  const lessonSlug = getSlug(lesson);
  const primaryPathSlug = getSlug(learningPath);

  const [{ progressForLessons, progressForCourses }, { fetchProgressForLessons, fetchProgressForCourses }] =
    useProgressState();

  useRedirectObject({
    ':courseId': courseSlug,
    ':lessonId': lessonSlug,
    ...(pathSlugFromUrl && { ':pathId': primaryPathSlug }),
  });

  useIntersectionObserverForProgressTracking();

  helmet.setHelmet({
    title: `${lesson?.title} | Learning - Atlassian Community`,
    seoTags: lesson?.seo,
  });

  const handleAudioPlayerOpen = useCallback(() => {
    setIsOpenedAudioPlayer((prev) => !prev);
  }, []);

  const handleLessonCompletion = (): void => {
    onLessonCompletion();
    setIsLessonRecentlyCompleted(true);
  };

  useEffect(() => {
    setIsLessonRecentlyCompleted(false);
  }, [lesson.sys.id]);

  const isCourse = !learningPath;

  useEffect(() => {
    if (!user) {
      return;
    }

    const lessonIds = isCourse
      ? course?.lessonsCollection.items.map((l) => l.sys.id) || [lesson.sys.id]
      : learningPath?.coursesCollection.items.flatMap((course) =>
        course.lessonsCollection.items.map((lesson) => lesson.sys.id),
      ) || [];

    if (lessonIds.length > 0) {
      fetchProgressForLessons(lessonIds, user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, course, learningPath]);

  // Scrolls down to section included in the URL as a hash
  useEffect(() => {
    const sectionSlug = location.hash.replace('#', '');
    const element = document.getElementById(sectionSlug);

    const timeoutId = setTimeout(() => {
      scrollToSection(element, isCourse);
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson, location]);

  useEffect(() => {
    if (!isLoading && !hasError && course && lesson) {
      sendScreenEvent(
        'lessonScreen',
        {
          lessonSlug,
          courseSlug,
          isStandaloneLesson,
        },
        user?.account_id,
      );
    }
  }, [user, isLoading, hasError, course, lesson, isStandaloneLesson, lessonSlug, courseSlug]);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (learningPath && !isCourse) {
      fetchProgressForCourses(learningPath, user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, learningPath, isCourse, isLessonRecentlyCompleted]);

  useEffect(() => {
    if (!progressForCourses) {
      return;
    }

    // Getting next course for the current learning path
    const fetchNextCourse = async (progressData: ProgressForCourses): Promise<void> => {
      if (learningPath && course) {
        const nextCourse = await getNextCourse(learningPath, course, progressData);

        setNextCourse(nextCourse);
      }
    };

    fetchNextCourse(progressForCourses);
  }, [course, learningPath, progressForCourses, user]);

  useEffect(() => {
    if (progressForLessons) {
      const courseSlug = getSlug(course);
      const lessonSlug = getSlug(lesson);
      const currentLessonProgress = progressForLessons.lessons.find((l) => l.lessonId === lesson?.sys.id);

      onLessonStarted(
        courseSlug,
        lessonSlug,
        currentLessonProgress?.status === LearningProgressStatus.IN_PROGRESS,
        currentLessonProgress?.status === LearningProgressStatus.COMPLETED,
        user?.account_id,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressForLessons, user, course]);

  useEffect(() => {
    let intervalId: ReturnType<typeof setTimeout> | undefined;

    if (progressForLessons && !hasNavigatedToSection.current && !isStandaloneLesson) {
      intervalId = navigateToSection(
        progressForLessons,
        lesson,
        course,
        navigate,
        location.pathname,
        isCourse,
        pathSlugFromUrl,
      );

      hasNavigatedToSection.current = true;
    }

    return () => intervalId && clearTimeout(intervalId);
  }, [progressForLessons, lesson, course, navigate, pathSlugFromUrl, isStandaloneLesson, location.pathname, isCourse]);

  useEffect(() => {
    setIsOpenedAudioPlayer(false);
  }, [lesson?.sys.id]);

  // Send a request to add the lesson to the list of recently viewed section when the user is redirected or has left the page
  useSaveRecentlyViewed(lesson?.sys.id, ContentType.LESSON, learningPath?.sys.id, course?.sys.id);

  const { quizBackground, nextLessonBackground } = getBackgroundColors(lesson);

  return (
    <>
      <main css={[basePageStyles, isSidebarVisible && baseMenuOpenStyles]} id="lesson-body" data-testid="lesson-page">
        <div css={css({ display: 'flex', flex: 1 })}>
          <div data-testid="audio-player-container" css={audioPlayerContainerStyles}>
            <Audio key={lesson?.sys.id} isOpened={isOpenedAudioPlayer} onClose={handleAudioPlayerOpen} />
          </div>
          {!isStandaloneLesson && course && (
            <Sidebar
              onChangeCurrentUrl={setCurrentUrl}
              isSidebarVisible={isSidebarVisible}
              onChangeSidebarVisibility={setIsSidebarVisible}
            />
          )}
          <div
            css={[
              placeholderStyles,
              isSidebarVisible && sidebarOpenStyles,
              isStandaloneLesson && css({ display: 'none' }),
            ]}
          />
          <div data-testid="lesson-page-body" css={css({ flexGrow: 1, flexShrink: 1 })}>
            {!isSidebarVisible && !isStandaloneLesson && (
              <div
                css={openMenuButtonContainerStyles}
                onClick={() => setIsSidebarVisible(true)}
                data-testid="open-menu-button"
              >
                <button css={openMenuButtonStyles}>
                  <Icon type="sidebar-expand" color={token('color.icon')} size="large" />
                  <span css={openMenuButtonTextStyles}>Menu</span>
                </button>
              </div>
            )}
            {isBreadcrumbVisible && (
              <BreadcrumbsContainer learningPath={learningPath} course={course} lessonSlug={lessonSlug} />
            )}
            <div css={[css({ background: '#FFF5D4' })]}>
              <div
                data-testid="lesson-header-container"
                css={[headerContainerStyles, isBreadcrumbVisible && breadcrumbMarginStyles]}
              >
                <LessonHeader
                  inspectMode={getInspectModeData(lesson, ['title'])}
                  heading={lesson?.title || ''}
                  tags={[
                    { label: `${lesson?.duration} min` },
                    ...(course?.learningLevel ? [{ label: course?.learningLevel }] : []),
                  ]}
                  urlToShare={currentUrl}
                  description={
                    <Text size="large">
                      <div css={markdownStyles}>
                        <MarkdownWrapper>{lesson?.description ? lesson.description : ''}</MarkdownWrapper>
                      </div>
                    </Text>
                  }
                  onListenClick={handleAudioPlayerOpen}
                />
              </div>
            </div>
            {lesson?.sectionListCollection.items?.map((section) => (
              <LessonSection key={section.sys.id} section={section} deviceWidth={deviceWidth} />
            ))}
            <div id="quiz">
              {!!lesson?.quiz && (
                <section
                  data-testid="quiz"
                  css={css({ padding: '80px 16px' })}
                  style={{ backgroundColor: quizBackground }}
                >
                  <Quiz onLessonCompletion={handleLessonCompletion} />
                </section>
              )}
            </div>
            <NextLesson nextLessonBackground={nextLessonBackground} />
          </div>
        </div>
      </main>
      {!isStandaloneLesson && (
        <BadgeModal
          nextCourse={nextCourse}
          isLessonRecentlyCompleted={isLessonRecentlyCompleted}
          badgeImg={course?.reward?.badgeCdnUrl || course?.reward?.badge?.[0]?.cdn_url || badge}
        />
      )}
      {!isStandaloneLesson && (
        <div
          css={[initialDarkenBackgroundStyles, isSidebarVisible && darkenBackgroundStyles]}
          data-testid="darken-background"
        />
      )}
    </>
  );
};

const placeholderStyles = css({
  flexShrink: 0,
  flexGrow: 0,
  width: 0,
  transition: 'width 0.3s ease',
});

const openMenuButtonContainerStyles = css({
  '@media (max-width: 480px)': {
    position: 'fixed',
    top: '56px',
    left: 0,
    width: '100% !important',
    backgroundColor: '#fff',
    height: '48px',
    zIndex: 4,
    padding: `${token('space.150')} ${token('space.200')}`,
  },

  [media.above.xs]: {
    display: 'none',
  },
});

const openMenuButtonStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#FFFFFF',
  border: 'none',
  cursor: 'pointer',

  svg: {
    width: '24px',
    height: '24px',
  },
});

const openMenuButtonTextStyles = css({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 400,
  color: token('color.text'),
  marginLeft: token('space.100'),
});

const sidebarOpenStyles = css({
  width: '420px',
  transition: 'width 0.3s ease',

  '@media (max-width: 1200px)': {
    width: 0,
  },
});

const basePageStyles = css({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const baseMenuOpenStyles = css({
  position: 'relative',
});

const headerContainerStyles = css({
  maxWidth: '720px',
  margin: '0 auto',
  padding: '0 16px',
  boxSizing: 'content-box',
  marginTop: '136px',

  '@media (max-width: 1279px)': {
    marginTop: '0',
  },
  [media.below.sm]: {
    marginTop: '48px',
  },
  [media.above.sm]: {
    padding: '0 24px',
  },
});

const breadcrumbMarginStyles = {
  marginTop: '204px',

  '@media (max-width: 1279px)': {
    marginTop: '68px',
  },

  '@media (max-width: 768px)': {
    marginTop: '104px',
  },
};

const audioPlayerContainerStyles = css({
  position: 'fixed',
  bottom: '72px',
  right: '72px',
  zIndex: 100,

  [media.below.xs]: {
    left: '50%',
    right: 'auto',
    bottom: '16px',
    transform: 'translateX(-50%)',
    minWidth: '340px',
  },
});

// TODO: this should be fixed in typography
const markdownStyles = css({
  'ul, ol, li': {
    marginTop: '16px',
  },
});

const initialDarkenBackgroundStyles = css({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100vh',
  backgroundColor: token('color.blanket'),
  zIndex: -1,
  opacity: 0,
  transition: 'opacity 0.3s ease',
});

const darkenBackgroundStyles = css({
  '@media (max-width: 1200px)': {
    opacity: 0.5,
    zIndex: 4,
  },
});
